import { Button, ModalSection } from '@monash/portal-react';
import cs from '../bulk-actions-shared.module.scss';
import { useForm } from 'react-hook-form';
import ControlledLinkDetailsList from '../controlled-link-details-list/ControlledLinkDetailsList';
import { useWatchCheckedLinksCount } from '../hooks';
import { useState } from 'react';
import { useAllLinkIdsMap } from '../../hooks/use-all-link-ids-map';
import { useSnackbar } from '../../../../providers/SnackbarProvider';
import { useLinksState } from '../../hooks/use-links-state';
import { LINK_STATUS } from '../../constants';

const BaseBulkDeleteModalContents = ({
  relevantLinks,
  onClose,
  deleteRequestFn,
  deleteLinkInParent,
  updateChecked,
  ...modalSectionProps
}) => {
  const { addSnackbar } = useSnackbar();

  // Use a local links state for the modal so that links persist
  // regardless of status changes or top level filters
  const { links: modalLinks, updateLink: updateLinkInModal } =
    useLinksState(relevantLinks);

  const [hasAttemptedDelete, setHasAttemptedDelete] = useState(false);
  const [isLoadingMap, setIsLoadingMap] = useState({});
  const isSomeLinkLoading = Object.values(isLoadingMap).some(Boolean);
  const [errorMap, setErrorMap] = useState({});
  const [disabledMap, setDisabledMap] = useState({});

  const enrichedModalLinks = modalLinks.map((link) => {
    return {
      ...link,
      isLoading: isLoadingMap[link.id],
      error: errorMap[link.id],
      disabled: disabledMap[link.id],
    };
  });

  const allIdsChecked = useAllLinkIdsMap({
    links: enrichedModalLinks,
    value: true,
  });

  const methods = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
    defaultValues: {
      checkedLinkIds: allIdsChecked,
    },
  });

  const { control, handleSubmit } = methods;

  const checkedLinksCount = useWatchCheckedLinksCount({
    name: 'checkedLinkIds',
    control,
  });

  const attemptDelete = async (link) => {
    const { id } = link;

    setIsLoadingMap((prevMap) => {
      return {
        ...prevMap,
        [id]: true,
      };
    });

    try {
      await deleteRequestFn(id);
      deleteLinkInParent(id);
      updateLinkInModal({
        ...link,
        status: LINK_STATUS.DELETED,
      });
      setDisabledMap((prevMap) => {
        return {
          ...prevMap,
          [id]: true,
        };
      });
    } catch (error) {
      setErrorMap((prevMap) => {
        return {
          ...prevMap,
          [id]: 'Failed to delete',
        };
      });

      const errorObj = new Error(error);
      errorObj.id = id;
      throw errorObj; // handle when all promises resolved
    } finally {
      setIsLoadingMap((prevMap) => {
        return {
          ...prevMap,
          [id]: false,
        };
      });
    }
  };

  const submitHandler = async (data) => {
    const { checkedLinkIds } = data;
    const checkedLinks = enrichedModalLinks.filter((link) => {
      const id = link.id;
      return checkedLinkIds[id];
    });

    const requests = checkedLinks.map((link) => {
      return attemptDelete(link);
    });

    setHasAttemptedDelete(true);
    const results = await Promise.allSettled(requests);

    const failedLinkIds = results
      .filter((result) => result.status === 'rejected')
      .map((result) => result.reason.id);

    const failedLinkIdsMap = Object.fromEntries(
      failedLinkIds.map((id) => [id, true])
    );

    updateChecked(failedLinkIdsMap);

    const numberFailed = failedLinkIds.length;

    if (numberFailed > 0) {
      addSnackbar({
        message: `${numberFailed} out of ${results.length} links failed`,
        type: 'error',
      });
    }
  };

  return (
    <ModalSection
      title={`Delete ${checkedLinksCount} link${
        checkedLinksCount === 1 ? '' : 's'
      }`}
      noPadding
      onClose={onClose}
      {...modalSectionProps}
    >
      <form onSubmit={handleSubmit(submitHandler)}>
        <ControlledLinkDetailsList
          links={enrichedModalLinks}
          name="checkedLinkIds"
          control={control}
        />
        <div className={cs.actionInput}>
          {hasAttemptedDelete ? (
            <Button
              variant="primary"
              size="medium"
              onClick={onClose}
              loading={isSomeLinkLoading}
            >
              Done
            </Button>
          ) : (
            <Button
              variant="delete"
              size="medium"
              type="submit"
              loading={isSomeLinkLoading}
              disabled={checkedLinksCount === 0}
            >
              Delete
            </Button>
          )}
        </div>
      </form>
    </ModalSection>
  );
};

export default BaseBulkDeleteModalContents;
